import type { CashDenominationRowDto } from './cash-denominations.dto.js';
import type { CashOperationIdentityDto } from './cash-operation-identity.dto.js';

export const cashOperatingModeValues = ['simple', 'denomination_inventory'] as const;
export type CashOperatingMode = (typeof cashOperatingModeValues)[number];

export const defaultCashierSelfRefillPerTransactionLimitMinor = 50_000;

export const cashScopeModeTransitionValues = [
  'none',
  'pending_activation',
  'ready_for_cutover',
  'pending_simple_transition',
] as const;
export type CashScopeModeTransition = (typeof cashScopeModeTransitionValues)[number];

export const cashModeScopeTypeValues = ['location', 'treasury'] as const;
export type CashModeScopeType = (typeof cashModeScopeTypeValues)[number];

export const cashModeActivationStatusValues = [
  'pending_activation',
  'ready_for_cutover',
  'completed',
  'cancelled',
] as const;
export type CashModeActivationStatus = (typeof cashModeActivationStatusValues)[number];

export interface CashOperatingConfigurationDto {
  companyId: string;
  targetOperatingMode: CashOperatingMode;
  usesCentralTreasury: boolean;
  allowDirectVaultIntake: boolean;
  cashierSelfRefillPerTransactionLimitMinor: number;
  allowDrawerToDrawer: boolean;
  revision: number;
  updatedAt: string;
}

export interface CashScopeModeStateDto {
  scopeType: CashModeScopeType;
  scopeId: string;
  effectiveMode: CashOperatingMode;
  transition: CashScopeModeTransition;
  configurationRevision: number;
  capturedEntitlementRevision: number;
}

export interface CashModeActivationCountDto {
  cashLocationId: string;
  cashSessionId: string | null;
  checkpointId: string;
  rows: CashDenominationRowDto[];
  totalMinor: number;
}

export interface CashModeActivationDto extends CashOperationIdentityDto {
  id: string;
  status: CashModeActivationStatus;
  scopeType: CashModeScopeType;
  scopeId: string;
  targetMode: CashOperatingMode;
  effectiveMode: CashOperatingMode;
  transition: CashScopeModeTransition;
  configurationRevision: number;
  capturedEntitlementRevision: number;
  counts: CashModeActivationCountDto[];
  activatedByUserId: string | null;
  activatedAt: string | null;
  cancelledByUserId: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCashOperatingConfigurationRequest {
  companyId: string;
  operationId: string;
  expectedRevision: number;
  targetOperatingMode: CashOperatingMode;
  usesCentralTreasury: boolean;
  allowDirectVaultIntake: boolean;
  cashierSelfRefillPerTransactionLimitMinor: number;
  allowDrawerToDrawer: boolean;
  occurredAt?: string;
}

export interface GetCashOperatingConfigurationRequest {
  companyId: string;
}

export interface GetCashOperatingConfigurationResponse {
  configuration: CashOperatingConfigurationDto;
}

export interface UpdateCashOperatingConfigurationResponse {
  configuration: CashOperatingConfigurationDto;
}

export interface GetCashScopeModeStateRequest {
  companyId: string;
  scopeType: CashModeScopeType;
  scopeId: string;
}

export interface GetCashScopeModeStateResponse {
  state: CashScopeModeStateDto;
  activation: CashModeActivationDto | null;
}

export interface StartCashModeActivationRequest {
  companyId: string;
  operationId: string;
  scopeType: CashModeScopeType;
  scopeId: string;
  configurationRevision: number;
  occurredAt?: string;
}

export interface StartCashModeActivationResponse {
  activation: CashModeActivationDto;
  state: CashScopeModeStateDto;
}

export interface RecordCashModeActivationCountsRequest {
  companyId: string;
  operationId: string;
  activationId: string;
  counts: CashModeActivationCountDto[];
  occurredAt?: string;
}

export interface RecordCashModeActivationCountsResponse {
  activation: CashModeActivationDto;
  state: CashScopeModeStateDto;
}

export interface ConfirmCashModeActivationRequest {
  companyId: string;
  operationId: string;
  activationId: string;
  occurredAt?: string;
}

export interface ConfirmCashModeActivationResponse {
  activation: CashModeActivationDto;
  state: CashScopeModeStateDto;
}

export interface CancelCashModeActivationRequest {
  companyId: string;
  operationId: string;
  activationId: string;
  reason: string;
  occurredAt?: string;
}

export interface CancelCashModeActivationResponse {
  activation: CashModeActivationDto;
  state: CashScopeModeStateDto;
}

export const cashControlEntitlementObservationSourceValues = ['cached', 'authoritative'] as const;
export type CashControlEntitlementObservationSource =
  (typeof cashControlEntitlementObservationSourceValues)[number];

export const cashControlEntitlementTransitionEffectValues = [
  'unchanged',
  'pending_simple_transition',
  'transitioned_to_simple',
  'control_restored_before_boundary',
] as const;
export type CashControlEntitlementTransitionEffect =
  (typeof cashControlEntitlementTransitionEffectValues)[number];

export interface ReconcileCashControlEntitlementRequest {
  companyId: string;
  operationId: string;
  scopeType: CashModeScopeType;
  scopeId: string;
  occurredAt?: string;
}

export interface ReconcileCashControlEntitlementResponse {
  state: CashScopeModeStateDto;
  effect: CashControlEntitlementTransitionEffect;
  safeBoundaryReached: boolean;
}
